import { useRoute, useRouter } from 'vue-router';
import type { PartialSearchParams } from '../types/search.types';
import { formatDateForApi } from '../utils/date';

type UpdateUrlParams = PartialSearchParams & {
  query?: Record<string, string | number | boolean | Date | null | undefined>;
};

export default function useUpdateUrl(customParams?: UpdateUrlParams) {
  const route = useRoute();
  const router = useRouter();

  const appendQueryValue = (query: Record<string, any>, key: string, value: unknown) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    if (value instanceof Date) {
      query[key] = formatDateForApi(value);
      return;
    }

    query[key] = value;
  };

  const updateUrl = async (params?: UpdateUrlParams) => {
    const searchParams = params || customParams;

    if (!searchParams) return;

    const query: Record<string, any> = {};

    if (searchParams.search) {
      query.term = searchParams.search;
    }

    if (searchParams.searchType) {
      query.searchType = searchParams.searchType;
    }

    if (searchParams.searchField) {
      query.searchField = searchParams.searchField;
    }

    if (searchParams.page !== undefined && searchParams.page > 1) {
      query.page = searchParams.page;
    }

    if (searchParams.sort?.sort && searchParams.sort?.order) {
      query.sort = searchParams.sort.sort;
      query.order = searchParams.sort.order;
    }

    if (searchParams.filter) {
      Object.entries(searchParams.filter).forEach(([key, value]) => {
        appendQueryValue(query, key, value);
      });
    }

    if (searchParams.query) {
      Object.entries(searchParams.query).forEach(([key, value]) => {
        appendQueryValue(query, key, value);
      });
    }

    const currentQuery = route.query;
    const currentQueryString = JSON.stringify(currentQuery);
    const newQueryString = JSON.stringify(query);

    if (currentQueryString !== newQueryString) {
      await router.replace({
        path: route.path,
        query,
      });
    }
  };

  return {
    updateUrl,
  };
}
